import { BaseEntity } from '@Framework/ui/BaseEntity';
import { useQaNamespace } from '@Framework/ui/quality-assurance';
import { Link as MuiLink } from '@material-ui/core';
import React, { FC, ReactNode, useMemo } from 'react';

/**
 * Link Interface
 */
export interface LinkProps extends BaseEntity {
  /**
   * Text displayed by the link
   */
  text: ReactNode;
  /**
   * href handler for Link (takes precedence over onClick)
   */
  href?: string;
  /**
   * Font size of link
   */
  fontSize?: number;
  /**
   * Click event function handler
   */
  onClick?: () => void;
  /**
   * Underlines the link text (defaults to true)
   */
  isUnderlined?: boolean;
  /**
   * Specifies the Shop Apotheke color to apply on the link
   */
  color?: 'primary' | 'secondary' | 'textPrimary' | 'textSecondary';
  /**
   * Whether the text should be shown as bold
   */
  isBold?: boolean;
  /**
   * Whether clicking a link should open a new tab
   */
  openInNewTab?: boolean;

  /**
   * data qa-id will prepend this to the current namespace if it is given
   */
  qaSuffix?: string;
}

// TODO: use Text component to access all styling
const Link: FC<LinkProps> = ({
  text,
  href,
  fontSize = 14,
  isUnderlined = true,
  isBold = false,
  onClick = () => undefined,
  style,
  color = 'textPrimary',
  qaSuffix,
  openInNewTab = false,
  ...props
}): React.ReactElement => {
  const linkStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize,
      fontWeight: isBold ? 'bold' : undefined,
      textDecoration: isUnderlined ? 'underline' : undefined,
      ...style
    }),
    [fontSize, style, isUnderlined, isBold]
  );

  const defaultSuffix = typeof text === 'string' ? text?.replace(/\s+/g, '-').toLowerCase() ?? 'link' : 'link';

  return (
    <MuiLink
      href={href}
      style={linkStyle}
      onClick={onClick}
      color={color}
      target={openInNewTab ? '_blank' : undefined}
      {...props}
      data-qa-id={useQaNamespace({ suffix: qaSuffix ?? defaultSuffix })}
    >
      {text}
    </MuiLink>
  );
};

export { Link };
